import type { IUserJSON } from 'external-api-types';
import axios from 'axios';
import type { CoreUser } from '../types/User';
import logger from './logging/logger';
import { apiFetch } from './ApiClient';
import { apiFetch as coreApiFetch } from './CoreApiClient';

export class USER_FEATURES {
    static TRIAL_DAYS_FROM_CREATION = 'trialDaysFromCreation' as const;

    static SIMPLIFIED_UI = 'simplifiedUI' as const;

    static PROJECT_DEPLOYMENT_SHOW_SURVEY = 'projectDeploymentShowSurvey' as const;

    static PROJECT_DEPLOYMENT_SHOW_VERIFY_EMAIL = 'projectDeploymentShowVerifyEmail' as const;

    static SETTINGS_CONNECT_DOMAIN_CARD = 'settingsConnectDomainCard' as const;

    static SUPPORT_EMAIL = 'supportEmail' as const;

    static DEFAULT_CUSTOMER_TIER = 'defaultCustomerTier' as const;
}

type AuthCallbackPayload = { coreUser: CoreUser | null; createUser: IUserJSON | null } | null;

interface AuthCallback {
    (users: AuthCallbackPayload): void;
}

class UserService {
    private authCallbacks: AuthCallback[] = [];

    onAuthChanged(cb: AuthCallback) {
        this.authCallbacks.push(cb);
        return () => {
            this.authCallbacks.filter((authCallback) => cb !== authCallback);
        };
    }

    private triggerAuthChanged(users: AuthCallbackPayload) {
        this.authCallbacks.forEach((cb: AuthCallback) => {
            cb(users);
        });
    }

    onUnauthorized() {
        this.triggerAuthChanged(null);
    }

    isUserAuthorized() {
        return apiFetch<{ isAuthenticated: boolean }>('/user/is-authenticated');
    }

    async authenticateUser(email: string, password: string) {
        const user = await apiFetch<IUserJSON>('/auth/login', { email, password }, 'post');
        this.triggerAuthChanged({ createUser: user, coreUser: null });
        return user;
    }

    async registerUser(email: string, password: string, data: $TSFixMe) {
        const user = await apiFetch<IUserJSON>(
            '/auth/register',
            {
                email,
                password,
                ...data
            },
            'post'
        );
        this.triggerAuthChanged({ createUser: user, coreUser: null });
        return user;
    }

    refreshAuth = () =>
        this.isUserAuthorized()
            .then(({ isAuthenticated }) => {
                if (isAuthenticated) {
                    return this.fetchCurrentUser().then(({ coreUser, createUser }) => {
                        this.triggerAuthChanged({ coreUser, createUser });
                        return { coreUser, createUser };
                    });
                }
                this.triggerAuthChanged(null);
                return Promise.resolve(null);
            })
            .catch((err) => {
                logger.debug(err);
                this.triggerAuthChanged(null);
                return null;
            });

    deauthenticateUser = () =>
        apiFetch<void>('/auth/logout').then(() => {
            this.triggerAuthChanged(null);
        });

    fetchCurrentUser() {
        return Promise.all([
            apiFetch<IUserJSON>('/user/my'),
            this.fetchCurrentCoreUser().catch((error) => {
                // Prevent erroring out for cases where there isn't a corresponding Core user yet
                if (axios.isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 401)) {
                    return null;
                }
                throw error;
            })
        ]).then(([createUser, coreUser]) => ({ createUser, coreUser }));
    }

    fetchCurrentCoreUser() {
        return coreApiFetch<CoreUser>({ endpoint: '/access-control/bb-api/api/v1/user' });
    }

    forgotPassword(email: string): Promise<{ message: string }> {
        return apiFetch('/auth/forgot-password', { email }, 'post');
    }

    resetPassword(newPassword: string, resetPasswordToken: string) {
        return apiFetch<{ message: string }>('/auth/reset-password', { newPassword, resetPasswordToken }, 'post');
    }

    initializeSSOLogin(
        domain: string,
        connectionId: string,
        state?: { redirect?: string; redirectOrigin?: string; initialReferrer?: string }
    ): Promise<{ success?: true; redirectURL: string; connections: { id: string; name: string }[] }> {
        return apiFetch('/auth/sso-login', { domain, connectionId, state }, 'post');
    }

    updatePassword(password: string, newPassword: string, newPasswordVerify: string) {
        return apiFetch<void>('/auth/update-password', { password, newPassword, newPasswordVerify }, 'post');
    }

    validateEmail(validationToken: string) {
        return apiFetch<{ displayEmail: string }>(`/auth/validate-email?validationToken=${encodeURIComponent(validationToken)}`, {}, 'get');
    }

    resendValidationEmail() {
        return apiFetch<{ message: string }>(`/emailvalidation/resend-validation-email`, {}, 'get');
    }

    getEmailValidations() {
        return apiFetch<{ email: string }>(`/emailvalidation/my`, {}, 'get');
    }

    deleteUser(password: string) {
        return apiFetch<void>('/user/my', { password }, 'DELETE');
    }

    updatePreferences(preferences: IUserJSON['preferences']) {
        return apiFetch<IUserJSON>('/user/my/prefs', { preferences }, 'PUT');
    }

    getPreference(user: IUserJSON, preference: keyof NonNullable<IUserJSON['preferences']>) {
        return user.preferences?.[preference];
    }

    getConnection(user: IUserJSON | null, connectionType: string) {
        const connections = user?.connections ?? [];
        return connections.find((con) => con.type === connectionType);
    }

    getFeature<F extends keyof IUserJSON['features']>(user: IUserJSON | null, featureName: F): IUserJSON['features'][F] {
        return user?.features[featureName];
    }

    isActiveTrial(user: IUserJSON) {
        const trialDays = this.totalTrialDays(user);
        if (!trialDays) {
            return false;
        }
        const trialDateMs = new Date(user.createdAt).getTime() + trialDays * 1000 * 60 * 60 * 24;
        return trialDateMs > Date.now() && new Date(trialDateMs);
    }

    hasTrial(user: IUserJSON) {
        return typeof this.totalTrialDays(user) !== 'undefined';
    }

    totalTrialDays(user: IUserJSON) {
        return this.getFeature(user, USER_FEATURES.TRIAL_DAYS_FROM_CREATION);
    }

    isEmailConfirmed(user: IUserJSON | null) {
        return user && user.authProvider === 'email' && typeof user.emailVerification !== 'undefined' ? user.emailVerification === 'verified' : true;
    }

    addSurvey(survey: IUserJSON['surveys'][number], overwrite = false) {
        return apiFetch<IUserJSON['surveys'][number]>(`/user/my/survey?overwrite=${overwrite}`, survey, 'POST');
    }

    async updatePlatformExclusivePreferences(referrer: string, user: IUserJSON) {
        let preferences = user?.preferences?.exclusivePlatforms;
        if (referrer === 'digitalocean') {
            const updatedUser = await this.updatePreferences({
                exclusivePlatforms: [
                    {
                        type: 'digitalocean'
                    }
                ]
            });
            preferences = updatedUser?.preferences?.exclusivePlatforms;
        }
        return Promise.resolve(preferences);
    }
}

export default new UserService();
